import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatCrewData, StatCrewRow, SeasonOrderEntry } from '../services/StatCrewservices';
import Spinner from './Spinner';
import SupportContactModal from './SupportContactModal';

interface StatCrewTableProps {
  data: StatCrewData | null;
  isLoading: boolean;
  error: string | null;
  username: string;
  selectedSport: string;
}

function getStagePillClass(stage: string, stageLabel?: string) {
  const label = (stageLabel || stage).toLowerCase();
  if (label.includes('complete')) return 'bg-emerald-100 text-emerald-700';
  if (label.includes("couldn't process") || label.includes('couldnt process')) {
    return 'bg-rose-100 text-rose-700';
  }
  if (label.includes('draft')) return 'bg-sky-100 text-sky-800';
  if (label.includes('needs attention')) return 'bg-amber-100 text-amber-800';
  const s = stage.toLowerCase();
  if (s.includes('inserted_available')) return 'bg-emerald-100 text-emerald-700';
  if (s.includes('invalid') || s.includes('error') || s.includes('fail') || s.includes('issues_during')) {
    return 'bg-rose-100 text-rose-600';
  }
  if (
    s.includes('validation_phase') ||
    s.includes('in_progress') ||
    s.includes('ready_to_insert') ||
    s.includes('progress') ||
    s.includes('pending')
  ) {
    return 'bg-amber-100 text-amber-700';
  }
  if (s.includes('ready_to_review') || s.includes('uploaded')) return 'bg-sky-100 text-sky-800';
  if (s.includes('complet') || s.includes('insert')) return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-600';
}

function getActionButtonClass(label: string) {
  const l = label.toLowerCase();
  if (l.includes('view preview')) return 'bg-emerald-500 text-white hover:bg-emerald-600';
  if (l.includes("couldn't process") || l.includes('couldnt process')) {
    return 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-500';
  }
  if (l.includes('fix')) return 'bg-rose-300 text-rose-950 hover:bg-rose-400';
  if (l.includes('continue')) return 'bg-cyan-500 text-white hover:bg-cyan-600';
  if (l.includes('upload')) return 'bg-cyan-500 text-white hover:bg-cyan-600';
  if (l.includes('resume')) return 'bg-amber-300 text-amber-950 hover:bg-amber-400';
  if (l.includes('view') || l.includes('detail')) return 'bg-emerald-500 text-white hover:bg-emerald-600';
  if (l.includes('retry')) return 'bg-violet-500 text-white hover:bg-violet-600';
  return 'bg-slate-800 text-white hover:bg-slate-700';
}

function EmptyTableState({ message }: { message: string }) {
  return (
    <div className="flex min-h-[220px] items-center justify-center rounded-[24px] border border-dashed border-slate-300 bg-white/70 px-6 py-10 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}

// Season data structure
interface SeasonBlock {
  season: string;
  rows: StatCrewRow[];
  hasHistory: boolean;
  isComplete: boolean;
}

// Check if the latest job for a season reached full integration.
function isSeasonComplete(rows: StatCrewRow[]): boolean {
  if (rows.length === 0) return false;
  return rows.some((row) => {
    const stage = String(row.stage ?? '')
      .trim()
      .replace(/^LifecycleState\./i, '')
      .toUpperCase()
      .replace(/\s+/g, '_');
    return stage === 'INSERTED_AVAILABLE';
  });
}

// Build season blocks from API data (includes ALL seasons)
function buildSeasonBlocks(data: StatCrewData): SeasonBlock[] {
  return data.seasons.map((season) => {
    const rows = data.rowsBySeason[season] ?? [];
    const isComplete = isSeasonComplete(rows);
    return { 
      season, 
      rows, 
      hasHistory: rows.length > 0, 
      isComplete 
    };
  });
}

function StatCrewTable({ data, isLoading, error, username, selectedSport }: StatCrewTableProps) {
  const navigate = useNavigate();
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [supportOpen, setSupportOpen] = useState(false);

  const seasonBlocks = data ? buildSeasonBlocks(data) : [];
  const seasonOrder: Record<string, SeasonOrderEntry> = data?.seasonOrder ?? {};
  const nextProcessableSeason = data?.nextProcessableSeason ?? null;
  // Server-side season-order gate state (STATCREW_ENFORCE_SEASON_ORDER). When
  // disabled, any season may be uploaded — show a season picker instead of the
  // single "next season" flow.
  const seasonOrderEnforced = data?.seasonOrderEnforced ?? true;

  const processableBlock = nextProcessableSeason
    ? seasonBlocks.find((block) => block.season === nextProcessableSeason)
    : null;
  const canUploadNextSeason = Boolean(
    processableBlock && !processableBlock.isComplete && !processableBlock.hasHistory
  );
  const nextSeasonInProgress = Boolean(
    processableBlock && !processableBlock.isComplete && processableBlock.hasHistory
  );

  const allEntitledComplete =
    seasonBlocks.length > 0 && seasonBlocks.every((block) => block.isComplete);

  const lockedSeasons = seasonBlocks.filter(
    (block) => seasonOrder[block.season]?.status === 'locked'
  );
  // All job rows (includes seasons with uploads even when every entitled season is "complete")
  const allHistoryRows = data
    ? Object.values(data.rowsBySeason).flat()
    : [];

  useEffect(() => {
    if (!seasonOrderEnforced) {
      // Free ordering: default to the newest season but keep any user choice.
      setSelectedSeason((prev) =>
        prev && seasonBlocks.some((b) => b.season === prev)
          ? prev
          : nextProcessableSeason || seasonBlocks[0]?.season || ''
      );
      return;
    }
    if (!canUploadNextSeason || !nextProcessableSeason) {
      setSelectedSeason('');
      return;
    }
    setSelectedSeason(nextProcessableSeason);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUploadNextSeason, nextProcessableSeason, seasonOrderEnforced, seasonBlocks.length]);

  const activeBlock = processableBlock;
  function handleUploadClick(season: string) {
    // The strict-order guard only applies while the server-side gate is on.
    if (seasonOrderEnforced && season !== nextProcessableSeason) {
      return;
    }
    navigate(
      `/dashboard/statcrew/upload?username=${encodeURIComponent(username)}&selectedSport=${encodeURIComponent(selectedSport)}&season=${encodeURIComponent(season)}`
    );
  }

  return (
    <>
    <section className="rounded-[28px] border border-slate-200 bg-[radial-gradient(circle_at_top,_rgba(148,163,184,0.18),_transparent_48%),linear-gradient(180deg,_#fdfefe_0%,_#f1f6ff_100%)] p-4 shadow-[0_30px_70px_-52px_rgba(15,23,42,0.65)] md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-slate-200/80 pb-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-500">{selectedSport}</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">StatCrew</h1>
        </div>

        <button
          type="button"
          onClick={() => setSupportOpen(true)}
          className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
        >
          Contact Support
        </button>

        {/* Season picker: free dropdown when the server-side order gate is off,
            otherwise the enforced "next season" display. */}
        {!seasonOrderEnforced && seasonBlocks.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Season to Upload <span className="normal-case tracking-normal text-amber-600">(order enforcement off)</span>
            </label>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm"
            >
              {seasonBlocks.map((block) => (
                <option key={block.season} value={block.season}>
                  {block.season}
                  {block.isComplete ? ' (completed)' : ''}
                </option>
              ))}
            </select>
          </div>
        ) : canUploadNextSeason && nextProcessableSeason ? (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Next Season to Upload</label>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm">
              {nextProcessableSeason}
            </div>
          </div>
        ) : null}
      </div>

      {/* Table area */}
      <div className="mt-5">
        {isLoading ? (
          <div className="flex min-h-[260px] items-center justify-center rounded-[24px] border border-slate-200 bg-white/75">
            <Spinner />
          </div>
        ) : error ? (
          <EmptyTableState message={error} />
        ) : seasonBlocks.length === 0 && allHistoryRows.length === 0 ? (
          <EmptyTableState message="No seasons available for this sport." />
        ) : (
          <div className="space-y-6">
            {allEntitledComplete && seasonBlocks.length > 0 && (
              <div className="rounded-[24px] border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-8">
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                    <svg className="h-6 w-6 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-emerald-900">All Seasons Complete!</p>
                    <p className="mt-1 text-sm text-emerald-700">
                      All entitled seasons have been uploaded and processed. Review upload history below.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {seasonOrderEnforced && nextSeasonInProgress && nextProcessableSeason && (
              <div className="rounded-[24px] border border-sky-200 bg-sky-50 px-5 py-4 text-sm text-sky-900">
                <p className="font-semibold">Season {nextProcessableSeason} in progress</p>
                <p className="mt-1 text-sky-800">
                  Resume or complete this season from upload history below before starting earlier seasons.
                </p>
              </div>
            )}

            {seasonOrderEnforced && lockedSeasons.length > 0 && (
              <div className="rounded-[24px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
                <p className="font-semibold">Seasons locked until higher seasons complete</p>
                <p className="mt-1 text-amber-800">
                  {nextProcessableSeason
                    ? `Complete season ${nextProcessableSeason} before uploading earlier seasons.`
                    : 'Complete the current season before uploading earlier seasons.'}
                </p>
                <p className="mt-2 text-xs text-amber-700">
                  Locked: {lockedSeasons.map((block) => block.season).join(', ')}
                </p>
              </div>
            )}

            {/* ─── UPLOAD SECTION: any season when order gate is off,
                     otherwise next processable season only ─── */}
            {(seasonOrderEnforced ? canUploadNextSeason && selectedSeason && activeBlock : Boolean(selectedSeason)) && (
              <div className="rounded-[24px] border border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-6">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-slate-900">Upload Files for {selectedSeason}</h3>
                  <p className="mt-1 text-xs text-slate-600">
                    {seasonOrderEnforced
                      ? 'Seasons must be processed in descending order. Upload this season before earlier years.'
                      : 'Season-order enforcement is disabled — any season can be uploaded.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleUploadClick(selectedSeason)}
                  className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-cyan-600 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Upload {selectedSeason} Season Files
                </button>
              </div>
            )}

            {/* ─── HISTORY SECTION: Show ALL uploads from ALL seasons ─── */}
            <div>
              <h3 className="mb-4 text-sm font-semibold text-slate-900">Upload History - All Seasons</h3>
              {allHistoryRows.length > 0 ? (
                <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_24px_54px_-40px_rgba(15,23,42,0.45)]">
                  <div className="overflow-x-auto">
                    <table className="min-w-[640px] w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400">Season</th>
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400">Stage</th>
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400">Last Updated</th>
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400">Owner</th>
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400">Next Action</th>
                          <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-widest text-slate-400">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allHistoryRows.map((row) => (
                          <tr key={`${row.season}-${row.jobId}`} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                            <td className="px-5 py-4 text-sm font-medium text-slate-700">{row.season}</td>
                            <td className="px-5 py-4 align-middle">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStagePillClass(row.stage, row.stageLabel)}`}
                              >
                                {row.stageLabel || row.stage}
                              </span>
                              <p className="mt-1 text-[10px] font-medium text-slate-400 uppercase tracking-wider">ID: {row.jobId}</p>
                            </td>
                            <td className="px-5 py-4 text-sm text-slate-500">{row.lastUpdated}</td>
                            <td className="px-5 py-4 text-sm text-slate-500">{row.owner}</td>
                            <td className="px-5 py-4 text-sm text-slate-500">{row.nextAction}</td>
                            <td className="px-5 py-4 text-right">
                              <button
                                type="button"
                                onClick={() => {
                                  if (row.actionUrl) {
                                    window.location.assign(row.actionUrl);
                                  }
                                }}
                                disabled={!row.actionUrl}
                                title={
                                  !row.actionUrl &&
                                  (row.actionLabel.toLowerCase().includes('processing') ||
                                    row.actionLabel.toLowerCase().includes("couldn't process"))
                                    ? row.actionLabel.toLowerCase().includes('processing')
                                      ? 'Pipeline is running — check back shortly'
                                      : 'Processing failed on our side — contact support if needed'
                                    : undefined
                                }
                                className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition ${
                                  row.actionUrl
                                    ? getActionButtonClass(row.actionLabel)
                                    : 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-500'
                                }`}
                              >
                                {row.actionLabel}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <EmptyTableState message="No upload history yet." />
              )}
            </div>
          </div>
        )}
      </div>
    </section>

    <SupportContactModal
      isOpen={supportOpen}
      onClose={() => setSupportOpen(false)}
      selectedSport={selectedSport}
      seasons={data?.seasons || []}
    />
    </>
  );
}

export default StatCrewTable;